import { useMemo, useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { apiErrorMessage } from '../../utils/errors';
import { useCurrentUser } from '../../features/auth/api';
import {
  useCustomerSubuserAddressAccess,
  useInviteCustomerSubuser,
  useListCustomerSubusers,
  useUpdateCustomerSubuserAddressAccess,
  useUpdateCustomerSubuserStatus,
  type CustomerSubuser,
  type CustomerSubuserAddressAccessItem,
} from '../../features/customer-subusers/api';
import { AppSidebar } from '../../components/AppSidebar/AppSidebar';

function statusLabel(status: string) {
  if (status === 'ACTIVE') return 'Active';
  if (status === 'INVITED') return 'Invited';
  if (status === 'DISABLED') return 'Disabled';
  return status || '—';
}

function statusBadgeStyle(status: string) {
  if (status === 'ACTIVE') {
    return { background: '#ecfdf5', border: '1px solid #a7f3d0', color: '#065f46' };
  }
  if (status === 'INVITED') {
    return { background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1d4ed8' };
  }
  if (status === 'DISABLED') {
    return { background: '#f9fafb', border: '1px solid #e5e7eb', color: '#374151' };
  }
  return { background: '#f3f4f6', border: '1px solid #e5e7eb' };
}

export default function CustomerSubusersTab() {
  const { data: me } = useCurrentUser();

  // Adjust this to whatever flag your current-user payload exposes
  const canManage = !!me?.type && me.type !== 'SUBUSER';

  const { data: subusers = [], isLoading } = useListCustomerSubusers(canManage);
  const invite = useInviteCustomerSubuser();
  const updateStatus = useUpdateCustomerSubuserStatus();

  const [showInvite, setShowInvite] = useState(false);
  const [managingSubuser, setManagingSubuser] = useState<CustomerSubuser | null>(null);

  const sorted = useMemo(() => {
    const rank = { ACTIVE: 0, INVITED: 1, DISABLED: 2 };
    return [...subusers].sort((a, b) => {
      const diff = (rank[a.status] ?? 9) - (rank[b.status] ?? 9);
      if (diff !== 0) return diff;
      return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    });
  }, [subusers]);

  if (!canManage) {
    return (
      <div className="card">
        <h2 className="card__title">Subusers</h2>
        <p className="card__subtitle">Only the primary customer user can manage subusers.</p>
        <div className="muted" style={{ marginTop: 12 }}>
          You don’t have permission to view this page.
        </div>
      </div>
    );
  }

  return (
    <div className="layout">
      <AppSidebar />
      <div className="card">
        <div className="flex-between" style={{ marginBottom: 12 }}>
          <div>
            <h2 className="card__title">Subusers</h2>
            <p className="card__subtitle">
              Invite subusers and control which delivery addresses they can use.
            </p>
          </div>

          <button className="btn btn--primary" onClick={() => setShowInvite(true)}>
            Invite subuser
          </button>
        </div>

        <div className="card card--bleed-top">
          {isLoading ? (
            <div className="skeleton" style={{ height: 220 }} />
          ) : sorted.length === 0 ? (
            <div className="muted" style={{ padding: 16 }}>
              No subusers yet.
            </div>
          ) : (
            <div className="table-scroll">
              <table className="table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Status</th>
                    <th>Address Access</th>
                    <th>Updated</th>
                    <th className="ta-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((u) => (
                    <tr key={u.id}>
                      <td>{u.name || u.user?.name || '—'}</td>
                      <td>{u.email}</td>
                      <td>
                        <span className="badge" style={statusBadgeStyle(u.status)}>
                          {statusLabel(u.status)}
                        </span>
                      </td>
                      <td>{u.enabledAddressCount ?? 0}</td>
                      <td>{new Date(u.updatedAt).toLocaleString()}</td>
                      <td className="ta-right">
                        <div className="actions" style={{ justifyContent: 'flex-end', gap: 8 }}>
                          <button
                            className="btn btn--secondary"
                            onClick={() => setManagingSubuser(u)}
                          >
                            Delivery options
                          </button>

                          {u.status === 'ACTIVE' ? (
                            <button
                              className="btn btn--danger"
                              disabled={updateStatus.isPending}
                              onClick={() => {
                                if (!window.confirm(`Disable ${u.email}?`)) return;

                                updateStatus.mutate(
                                  { customerUserId: u.id, status: 'DISABLED' },
                                  {
                                    onSuccess: () => toast.success('Subuser disabled.'),
                                    onError: (err) =>
                                      toast.error(
                                        err?.response?.data?.error || 'Failed to disable subuser'
                                      ),
                                  }
                                );
                              }}
                            >
                              Disable
                            </button>
                          ) : u.status === 'DISABLED' ? (
                            <button
                              className="btn btn--primary"
                              disabled={updateStatus.isPending}
                              onClick={() => {
                                updateStatus.mutate(
                                  { customerUserId: u.id, status: 'ACTIVE' },
                                  {
                                    onSuccess: () => toast.success('Subuser re-enabled.'),
                                    onError: (err) =>
                                      toast.error(
                                        err?.response?.data?.error || 'Failed to re-enable subuser'
                                      ),
                                  }
                                );
                              }}
                            >
                              Enable
                            </button>
                          ) : (
                            <button
                              className="btn btn--ghost"
                              disabled
                              title="Invited subusers cannot be activated until they accept the invite"
                            >
                              Pending invite
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {showInvite && (
          <InviteCustomerSubuserModal
            onClose={() => setShowInvite(false)}
            onInvite={async (payload: any) => {
              try {
                await invite.mutateAsync(payload);
                toast.success('Invite sent.');
                setShowInvite(false);
              } catch (e) {
                toast.error(apiErrorMessage(e, 'Invite failed'));
              }
            }}
            isInviting={invite.isPending}
          />
        )}

        {managingSubuser && (
          <ManageSubuserAddressAccessModal
            subuser={managingSubuser}
            onClose={() => setManagingSubuser(null)}
          />
        )}
      </div>
    </div>
  );
}

function InviteCustomerSubuserModal({
  onClose,
  onInvite,
  isInviting,
}: {
  onClose: () => void;
  onInvite: (payload: any) => Promise<void> | void;
  isInviting?: boolean;
}) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');

  const canSubmit = email.trim().length > 3 && name.trim().length > 0;

  return (
    <div className="modal" role="dialog" aria-modal="true">
      <div className="modal__backdrop" onClick={onClose} />
      <div className="modal__panel" style={{ maxWidth: 520 }}>
        <div className="modal__header">
          <h3 className="modal__title">Invite subuser</h3>
          <button className="btn btn--ghost" onClick={onClose}>
            Close
          </button>
        </div>

        <div className="modal__body">
          <div className="form-control">
            <label>Name</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Jane Smith"
              autoFocus
            />
          </div>

          <div className="form-control" style={{ marginTop: 12 }}>
            <label>Email</label>
            <input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@company.com"
              type="email"
            />
          </div>

          <div className="muted" style={{ marginTop: 10 }}>
            The subuser will receive an invite and can be assigned delivery addresses after
            creation.
          </div>
        </div>

        <div className="modal__footer">
          <div className="actions" style={{ gap: 8 }}>
            <button
              className="btn btn--primary"
              disabled={!canSubmit || isInviting}
              onClick={() =>
                onInvite({
                  email: email.trim(),
                  name: name.trim(),
                })
              }
            >
              {isInviting ? 'Sending…' : 'Send invite'}
            </button>
            <button className="btn btn--ghost" onClick={onClose}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ManageSubuserAddressAccessModal({
  subuser,
  onClose,
}: {
  subuser: CustomerSubuser;
  onClose: () => void;
}) {
  const { data, isLoading } = useCustomerSubuserAddressAccess(subuser.id, true);
  const updateAccess = useUpdateCustomerSubuserAddressAccess(subuser.id);
  const [localAddresses, setLocalAddresses] = useState<CustomerSubuserAddressAccessItem[]>([]);

  useEffect(() => {
    setLocalAddresses(data?.addresses || []);
  }, [data]);

  function toggleAddress(addressId: string) {
    setLocalAddresses((prev) =>
      prev.map((item) => (item.id === addressId ? { ...item, isEnabled: !item.isEnabled } : item))
    );
  }

  async function handleSave() {
    try {
      await updateAccess.mutateAsync({
        addresses: localAddresses.map((a) => ({
          deliveryAddressId: a.id,
          isEnabled: !!a.isEnabled,
        })),
      });

      toast.success('Delivery options updated.');
      onClose();
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to update delivery options'));
    }
  }

  return (
    <div className="modal" role="dialog" aria-modal="true">
      <div className="modal__backdrop" onClick={onClose} />
      <div className="modal__panel" style={{ maxWidth: 720 }}>
        <div className="modal__header">
          <div>
            <h3 className="modal__title">Manage delivery options</h3>
            <div className="muted" style={{ marginTop: 4 }}>
              {subuser.name || subuser.user?.name || 'Unnamed user'} · {subuser.email}
            </div>
          </div>

          <button className="btn btn--ghost" onClick={onClose}>
            Close
          </button>
        </div>

        <div className="modal__body">
          {isLoading ? (
            <div className="skeleton" style={{ height: 220 }} />
          ) : !localAddresses.length ? (
            <div className="muted">No customer delivery addresses found.</div>
          ) : (
            <div className="stack" style={{ gap: 10 }}>
              {localAddresses.map((addr) => (
                <label
                  key={addr.id}
                  className="card"
                  style={{
                    padding: 12,
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 12,
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={!!addr.isEnabled}
                    onChange={() => toggleAddress(addr.id)}
                    style={{ marginTop: 4 }}
                  />

                  <div>
                    <div style={{ fontWeight: 600 }}>{addr.label || 'Delivery address'}</div>
                    <div className="muted" style={{ marginTop: 4 }}>
                      {[addr.addressLine1, addr.addressLine2].filter(Boolean).join(', ')}
                      {[addr.addressLine1, addr.addressLine2].some(Boolean) &&
                      (addr.city || addr.state || addr.postalCode)
                        ? ', '
                        : ''}
                      {[addr.city, addr.state, addr.postalCode].filter(Boolean).join(', ')}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          )}
        </div>

        <div className="modal__footer">
          <div className="actions" style={{ gap: 8 }}>
            <button
              className="btn btn--primary"
              disabled={isLoading || updateAccess.isPending}
              onClick={handleSave}
            >
              {updateAccess.isPending ? 'Saving…' : 'Save'}
            </button>
            <button className="btn btn--ghost" onClick={onClose}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
